import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { WeatherLayerType } from "./weatherLayer";
import { RootState } from "../store";

// Type definitions for each layer's tooltip data
export type WeatherTooltipData = {
  value: number;
  displayValue: string;
  layerType: WeatherLayerType;
};

export type PublicLandTooltipData = {
  unitName: string;
  agencyName?: string;
};

// Record type to allow any layer to add tooltip data
export type LayerTooltipData = Record<string, any>;

type MapTooltipState = {
  position: { x: number; y: number } | null;
  layers: LayerTooltipData;
};

const initialState: MapTooltipState = {
  position: null,
  layers: {},
};

const mapTooltip = createSlice({
  name: "mapTooltip",
  initialState,
  reducers: {
    setTooltipPosition: (
      state,
      action: PayloadAction<{ x: number; y: number } | null>
    ) => {
      state.position = action.payload;
    },
    setLayerTooltipData: (
      state,
      action: PayloadAction<{ key: string; data: any | null }>
    ) => {
      if (action.payload.data === null) {
        delete state.layers[action.payload.key];
      } else {
        state.layers[action.payload.key] = action.payload.data;
      }
    },
    clearLayerTooltipData: (
      state,
      action: PayloadAction<string>
    ) => {
      delete state.layers[action.payload];
    },
    clearAllTooltips: (state) => {
      state.position = null;
      state.layers = {};
    },
  },
});

export const {
  setTooltipPosition,
  setLayerTooltipData,
  clearLayerTooltipData,
  clearAllTooltips,
} = mapTooltip.actions;

export default mapTooltip.reducer;

// Selectors
const selectTooltipPosition = (state: RootState) => state.mapTooltip.position;
const selectTooltipLayers = (state: RootState) => state.mapTooltip.layers;

export const useTooltipPosition = () => {
  return useSelector(selectTooltipPosition);
};

export const useTooltipLayers = () => {
  return useSelector(selectTooltipLayers);
};
